import React, { useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { usePropertyAccessControlGroups } from "./hooks/usePropertyData";
import { Switch } from "components/ui/switch";
import { SortableTableHead } from "components/shared/SortableTableHead";

const DEFAULT_PAGE_SIZE = 10;

type SortField = "name" | "type" | "locks" | "updated";
type SortDirection = "asc" | "desc";

export default function PropertyAccessControlGroups() {
  const { externalId } = useParams<{ externalId: string }>();
  const { data, isLoading, isError } = usePropertyAccessControlGroups(
    externalId!,
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [showUnits, setShowUnits] = useState(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0);
  };

  // Filter, sort, and paginate data
  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];

    // Fuzzy search filter
    let filtered = data;
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((group) => {
        const name = (group.name ?? "").toLowerCase();
        const type = group.type.toLowerCase();
        const id = group.id.toLowerCase();
        return (
          name.includes(searchLower) ||
          type.includes(searchLower) ||
          id.includes(searchLower)
        );
      });
    }

    // Show units filter
    if (!showUnits) {
      filtered = filtered.filter((group) => group.type !== "unit");
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = (a.name ?? "").localeCompare(b.name ?? "");
          break;
        case "type":
          comparison = a.type.localeCompare(b.type);
          break;
        case "locks":
          comparison = (a.locks?.length ?? 0) - (b.locks?.length ?? 0);
          break;
        case "updated":
          comparison = new Date(a.updateTimestamp).getTime() - new Date(b.updateTimestamp).getTime();
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [data, search, sortField, sortDirection, showUnits]);

  const paginatedData = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return filteredAndSortedData.slice(start, end);
  }, [filteredAndSortedData, page, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredAndSortedData.length / pageSize);
  }, [filteredAndSortedData, pageSize]);

  return (
    <AdminTable
      aria-label="Property access control groups"
      toolbar={
        <div className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-end sm:justify-between">
          <label className="grid w-full gap-1 text-xs font-semibold tracking-wide uppercase text-slate-500 sm:max-w-sm">
            Search groups
            <input
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              placeholder="Search by name, type, or ID"
            />
          </label>
          <div className="flex items-center gap-2 pb-2">
            <label
              className="text-sm font-medium text-slate-700"
              htmlFor="property-access-groups-show-units"
            >
              Show Units
            </label>
            <Switch
              checked={showUnits}
              id="property-access-groups-show-units"
              onCheckedChange={(checked) => {
                setShowUnits(checked);
                setPage(0);
              }}
            />
          </div>
        </div>
      }
      footer={
        !isLoading && !isError && totalPages > 1 ? (
          <AdminTablePagination
            currentPage={page}
            pageSize={pageSize}
            visibleRowCount={paginatedData.length}
            disableNext={page >= totalPages - 1}
            onNext={() => setPage((currentPage) => currentPage + 1)}
            onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
          />
        ) : undefined
      }
    >
      <thead>
        <tr>
          <SortableTableHead
            active={sortField === "name"}
            ariaLabel="Sort by name"
            direction={sortField === "name" ? sortDirection : "asc"}
            onSort={() => handleSort("name")}
            scope="col"
          >
            Name
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "type"}
            ariaLabel="Sort by type"
            direction={sortField === "type" ? sortDirection : "asc"}
            onSort={() => handleSort("type")}
            scope="col"
          >
            Type
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "locks"}
            ariaLabel="Sort by lock count"
            direction={sortField === "locks" ? sortDirection : "asc"}
            onSort={() => handleSort("locks")}
            scope="col"
          >
            Locks
          </SortableTableHead>
          <SortableTableHead
            active={sortField === "updated"}
            ariaLabel="Sort by updated date"
            direction={sortField === "updated" ? sortDirection : "asc"}
            onSort={() => handleSort("updated")}
            scope="col"
          >
            Updated
          </SortableTableHead>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={4}>
              <div className="flex items-center justify-center min-h-80 text-sm text-slate-500">
                Loading access control groups...
              </div>
            </td>
          </tr>
        ) : isError ? (
          <tr>
            <td colSpan={4}>
              <div className="flex items-center justify-center px-4 text-sm text-center min-h-80 text-rose-700">
                Error loading access control groups.
              </div>
            </td>
          </tr>
        ) : !data || data.length === 0 ? (
          <tr>
            <td colSpan={4} className="py-16 text-sm text-center text-slate-500">
              No access control groups found for this property.
            </td>
          </tr>
        ) : filteredAndSortedData.length === 0 ? (
          <tr>
            <td colSpan={4} className="py-16 text-sm text-center text-slate-500">
              No access control groups match your search.
            </td>
          </tr>
        ) : (
          paginatedData.map((group) => (
            <tr key={group.id}>
              <td>
                <div>
                  <RouterLink to={`/admin/accessControlGroups/${group.id}`}>
                    {group.name ?? "-"}
                  </RouterLink>
                </div>
                <div className="pt-1 font-mono text-xs text-slate-500">
                  {group.id}
                </div>
              </td>
              <td>{group.type}</td>
              <td>{group.locks?.length ?? 0}</td>
              <td>{new Date(group.updateTimestamp).toLocaleDateString()}</td>
            </tr>
          ))
        )}
      </tbody>
    </AdminTable>
  );
}
